import { useCallback, useEffect, useRef } from "react";

/**
 * 防抖 hook - 延迟执行函数，避免频繁更新 Lexical 节点
 *
 * 修复 Lexical error #195
 */
export function useDebouncedUpdate<TArgs extends unknown[]>(
  updateFn: (...args: TArgs) => void,
  getEditor: () => { update: (cb: () => void) => void } | null,
  delay: number,
): (...args: TArgs) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const argsRef = useRef<TArgs | null>(null);
  const updateFnRef = useRef(updateFn);
  const getEditorRef = useRef(getEditor);

  useEffect(() => {
    updateFnRef.current = updateFn;
    getEditorRef.current = getEditor;
  }, [updateFn, getEditor]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: TArgs) => {
      argsRef.current = args;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (argsRef.current) {
          const argsToApply = argsRef.current;
          argsRef.current = null;

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const editor = getEditorRef.current();
              if (editor) {
                editor.update(() => {
                  updateFnRef.current(...argsToApply);
                });
              }
            });
          });
        }
      }, delay);
    },
    [delay],
  );
}

/**
 * 立即更新 hook - 用于必填字段，确保 validationService 能立即读到最新值
 */
export function useImmediateUpdate<T extends (...args: Parameters<T>) => ReturnType<T>>(
  updateFn: T,
  getEditor: () => { update: (cb: () => void) => void } | null,
): (...args: Parameters<T>) => void {
  const updateFnRef = useRef(updateFn);
  const getEditorRef = useRef(getEditor);

  useEffect(() => {
    updateFnRef.current = updateFn;
    getEditorRef.current = getEditor;
  }, [updateFn, getEditor]);

  return useCallback((...args: Parameters<T>) => {
    const editor = getEditorRef.current();
    if (editor) {
      Promise.resolve().then(() => {
        editor.update(() => {
          updateFnRef.current(...args);
        });
      });
    }
  }, []);
}
